import { Alert, Anchor, Button, PasswordInput, Stack } from "@mantine/core";
import { schemaResolver, useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { client, errorMessage, isApiError, unwrap } from "@/api";

import { AuthCard } from "./AuthCard";

export const newPasswordSchema = z
  .object({ password: z.string().min(8, "At least 8 characters"), confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], message: "Passwords do not match" });

/** /reset-password?token=…: also how new users set their first password (spec §3.3). */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(token ? null : "invalid");
  const form = useForm({
    initialValues: { password: "", confirm: "" },
    validate: schemaResolver(newPasswordSchema, { sync: true }),
  });

  const submit = form.onSubmit(async ({ password }) => {
    setError(null);
    try {
      await unwrap(client.POST("/api/v1/auth/password-reset/confirm", { body: { token, new_password: password } }));
      notifications.show({ color: "green", message: "Password set. Sign in with your new password." });
      navigate("/login", { replace: true });
    } catch (e) {
      setError(isApiError(e, "RESET_TOKEN_INVALID") ? "invalid" : errorMessage(e));
    }
  });

  if (error === "invalid") {
    return (
      <AuthCard title="Link invalid or expired">
        <Stack>
          <Alert color="red">This reset link is invalid or has expired.</Alert>
          <Anchor component={Link} to="/forgot">
            Request a new link
          </Anchor>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set a new password">
      <form onSubmit={submit}>
        <Stack>
          <PasswordInput
            label="New password"
            autoComplete="new-password"
            required
            {...form.getInputProps("password")}
          />
          <PasswordInput
            label="Repeat new password"
            autoComplete="new-password"
            required
            {...form.getInputProps("confirm")}
          />
          {error && <Alert color="red">{error}</Alert>}
          <Button type="submit" loading={form.submitting}>
            Set password
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
